import { Dialog, DialogContent } from "@/ui-components/ui/dialog";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { Button } from "@/ui-components/ui/button";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Loader2, Pencil, Save, Trash2 } from "lucide-react";
import { ReactNode, useState, useEffect } from "react";
import { cn } from "@/lib/utils/cn";

/**
 * Defines the current state of the details dialog.
 * - "details": Read-only view
 * - "edit": Edit mode with form
 */
export type DetailsBaseDialogMode = "details" | "edit";

export interface DetailsBaseDialogProps {
  /** Determines if the dialog is visible. */
  open: boolean;
  /** Callback triggered when the dialog wants to close. `refetchData` indicates if a reload is necessary. */
  onClose: (refetchData: boolean) => void;
  /** The initial mode the dialog should open in. Defaults to "details". */
  mode?: DetailsBaseDialogMode;

  // --- Header Props ---
  /** The Lucide icon to display in the header. */
  icon: any;
  /** The main title of the dialog. */
  title: string;
  /** A short description shown below the title. */
  description: string;

  // --- Delete Dialog Props ---
  /** Title for the deletion confirmation dialog. */
  deleteTitle?: string;
  /** The name or title of the item being deleted (e.g. "Max Mustermann"). */
  deleteItemTitle?: string;
  /** The label for the identifier of the item. Defaults to "ID". */
  deleteItemTypeLabel?: string;
  /** The actual identifier value of the item being deleted. */
  deleteItemTypeValue?: string;
  /** Callback triggered when the user confirms the deletion. */
  onDeleteConfirm?: () => Promise<void> | void;
  /** Indicates if a delete operation is currently running. */
  isDeleting?: boolean;

  // --- Customization Props ---
  /** If true, the delete button is hidden. */
  hideDelete?: boolean;
  /** If true, the edit button is hidden. */
  hideEdit?: boolean;
  /** Custom label for the edit button. Defaults to "Bearbeiten". */
  editButtonLabel?: string;
  /** Custom icon for the edit button. Defaults to Pencil. */
  editButtonIcon?: any;
  /** Custom label for the save button. Defaults to "Speichern". */
  saveButtonLabel?: string;
  /** Custom action buttons to display in the details mode footer (rendered next to the edit button). */
  customDetailsActions?: ReactNode;

  // --- Content Props ---
  /** The content to render in the read-only "details" mode. */
  detailsContent: ReactNode;
  /** A render prop that provides the form content for the "edit" mode. Receives a callback to signal a successful save. */
  editContent?: (onSaveSuccess: () => void) => ReactNode;

  // --- Footer state for edit mode ---
  /** Indicates if a save operation is currently running in edit mode. */
  isSaving?: boolean;
  /** Indicates if the form is valid and can be submitted in edit mode. */
  canSave?: boolean;
  /** The ID of the form element to connect the submit button with the form outside of the footer. */
  formId?: string;

  /** The maximum width of the dialog. Defaults to "2xl". */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl";
  /** Optional additional CSS classes for the DialogContent. */
  className?: string;
}

/**
 * A highly standardized base dialog for viewing and editing entities.
 * Features:
 * - Built-in switching between "details" (read-only) and "edit" modes.
 * - Standardized footer action buttons based on the current mode and user permissions.
 * - Built-in integration with the `StandardDeleteDialog` that intercepts the delete action.
 * - Smart state handling: If opened directly in edit mode, saving will close the dialog.
 *   If opened in details mode and switched to edit, saving will return to details mode.
 */
export const DetailsBaseDialog = ({
  open,
  onClose,
  mode = "details",
  icon,
  title,
  description,
  deleteTitle,
  deleteItemTitle,
  deleteItemTypeLabel = "ID",
  deleteItemTypeValue,
  onDeleteConfirm,
  isDeleting = false,
  hideDelete = false,
  hideEdit = false,
  editButtonLabel = "Bearbeiten",
  editButtonIcon: EditIcon = Pencil,
  saveButtonLabel = "Speichern",
  customDetailsActions,
  detailsContent,
  editContent,
  isSaving = false,
  canSave = true,
  formId,
  maxWidth = "2xl",
  className,
}: DetailsBaseDialogProps) => {
  const [currentMode, setCurrentMode] = useState<DetailsBaseDialogMode>(mode);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // keep track of the mode the dialog was opened with
  const [initialMode] = useState<DetailsBaseDialogMode>(mode);

  useEffect(() => {
    setCurrentMode(mode);
  }, [mode, open]);

  const handleClose = (refetchData = false) => {
    onClose(refetchData);
  };

  const handleCancelEdit = () => {
    if (initialMode === "edit") {
      handleClose(false);
    } else {
      setCurrentMode("details");
    }
  };

  const handleSaveSuccess = () => {
    if (initialMode === "edit") {
      handleClose(true);
    } else {
      setCurrentMode("details");
    }
  };

  const handleDelete = async () => {
    // 1. First set delete dialog to false to close the modal
    setDeleteDialogOpen(false);

    // 2. Await a small timeout to let the dialog animation finish and unmount properly
    // This prevents the React component tree from remounting or firing extra events
    // while the mutation is running, which can cause double API calls.
    await new Promise((resolve) => setTimeout(resolve, 100));

    // 3. Execute the actual delete action
    if (onDeleteConfirm) {
      await onDeleteConfirm();
    }
  };

  const maxWidthClass = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
  }[maxWidth];

  return (
    <>
      <Dialog open={open} onOpenChange={(val) => !val && handleClose(false)}>
        <DialogContent
          className={cn(
            `${maxWidthClass} border-0 shadow-2xl p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0`,
            className,
          )}
        >
          <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
            <StandardDialogHeader
              icon={icon}
              title={title}
              description={description}
              className="p-0 border-none"
            />
          </div>

          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {currentMode === "edit" ? (
              <>
                {editContent && editContent(handleSaveSuccess)}
                <div className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="flex w-full sm:w-auto">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
                    >
                      Abbrechen
                    </Button>
                  </div>
                  <div className="flex w-full sm:w-auto sm:justify-end">
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ENTITIES}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <Button
                        type="submit"
                        form={formId}
                        disabled={isSaving || !canSave}
                        className="h-11 w-full px-6 sm:px-8 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all min-w-[120px] sm:min-w-[140px] sm:w-auto"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            <span className="hidden sm:inline">
                              {saveButtonLabel}...
                            </span>
                            <span className="sm:hidden">...</span>
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            {saveButtonLabel}
                          </>
                        )}
                      </Button>
                    </PermissionFeatureWrapper>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5 sm:space-y-6">
                  {detailsContent}
                </div>
                <div className="px-5 sm:px-6 py-4 flex items-center justify-between border-t border-gray-100 shrink-0 bg-gradient-to-t from-background via-background to-background/95">
                  {hideDelete ? (
                    <div />
                  ) : (
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ENTITIES}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <Button
                        variant="ghost"
                        size="default"
                        onClick={() => setDeleteDialogOpen(true)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-11 px-3 sm:px-5 rounded-xl font-bold text-sm transition-all"
                      >
                        <Trash2 className="w-4.5 h-4.5 sm:mr-2" />
                        <span className="hidden sm:inline">Löschen</span>
                      </Button>
                    </PermissionFeatureWrapper>
                  )}
                  <div className="flex gap-2 sm:gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => handleClose(false)}
                      className="h-11 px-5 sm:px-6 rounded-xl font-semibold text-sm text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900"
                    >
                      <span className="hidden sm:inline">Schließen</span>
                      <span className="sm:hidden">Abbrechen</span>
                    </Button>
                    {customDetailsActions}
                    {!hideEdit && (
                      <PermissionFeatureWrapper
                        requiredPermission={Permission.MANAGE_ENTITIES}
                        requiredPlan={PlanKey.BASIC}
                      >
                        <Button
                          onClick={() => setCurrentMode("edit")}
                          size="default"
                          className="h-11 px-6 sm:px-8 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all"
                        >
                          <EditIcon className="w-4 h-4 mr-2" />
                          {editButtonLabel}
                        </Button>
                      </PermissionFeatureWrapper>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <StandardDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDelete={handleDelete}
        title={deleteTitle || ""}
        itemTitle={deleteItemTitle || ""}
        itemTypeLabel={deleteItemTypeLabel}
        itemTypeValue={deleteItemTypeValue}
        isLoading={isDeleting}
      />
    </>
  );
};
